

import { useState, useRef, useCallback } from "react"
import { Upload, X, FileArchive, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface ActionFileUploadProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  label?: string
  accept?: string
  allowedExtensions?: string[]
  allowMultiple?: boolean
}

const DEFAULT_ALLOWED_EXTENSIONS = [".zip", ".xml"]
const DEFAULT_ACCEPT = ".zip,.xml,application/zip,application/x-zip-compressed,application/xml,text/xml"

function isAllowedFile(file: File, allowedExtensions: string[]): boolean {
  const lowerName = file.name.toLowerCase()
  return allowedExtensions.some((extension) => lowerName.endsWith(extension))
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(fileName: string) {
  return fileName.toLowerCase().endsWith(".zip") ? FileArchive : FileText
}

export function ActionFileUpload({
  files,
  onFilesChange,
  label = "Arraste arquivos ZIP ou XML, ou clique para selecionar",
  accept = DEFAULT_ACCEPT,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  allowMultiple = true,
}: ActionFileUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback(
    (incomingFiles: File[]) => {
      const validFiles = incomingFiles.filter((file) => isAllowedFile(file, allowedExtensions))
      if (validFiles.length === 0) return

      if (allowMultiple) {
        onFilesChange([...files, ...validFiles])
        return
      }

      onFilesChange([validFiles[0]])
    },
    [allowMultiple, allowedExtensions, files, onFilesChange]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragging(false)
      addFiles(Array.from(event.dataTransfer.files))
    },
    [addFiles]
  )

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      addFiles(Array.from(event.target.files || []))
      if (inputRef.current) inputRef.current.value = ""
    },
    [addFiles]
  )

  const removeFile = useCallback(
    (indexToRemove: number) => {
      onFilesChange(files.filter((_, fileIndex) => fileIndex !== indexToRemove))
    },
    [files, onFilesChange]
  )

  return (
    <div className="space-y-2">
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={(event) => {
          event.preventDefault()
          setIsDragging(false)
        }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-4 transition-all",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/40 hover:bg-accent/30"
        )}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") inputRef.current?.click()
        }}
      >
        <Upload className={cn("h-5 w-5", isDragging ? "text-primary" : "text-muted-foreground")} />
        <p className="text-xs text-muted-foreground text-center break-words">{label}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={allowMultiple}
          className="hidden"
          onChange={handleSelect}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, index) => {
            const FileIcon = getFileIcon(file.name)
            return (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded-lg bg-accent/50 px-3 py-2"
              >
                <FileIcon className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 min-w-0 truncate text-xs text-foreground">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(file.size)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={(event) => {
                    event.stopPropagation()
                    removeFile(index)
                  }}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remover arquivo</span>
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
